import { motion } from 'motion/react';
import { useState } from 'react';
import { CDN } from '../lib/cdn';
import { CdnImage } from './CdnImage';

const GALLERY_IMAGES = Object.values(CDN.gallery);

const ease = [0.16, 1, 0.3, 1] as const;

const GALLERY = [
  { slug: 'CYBER-ARENA', sh: 'EXT. CYBER-ARENA - NIGHT', line: 'Action shot as the warrior lunges forward and swings a crackling energy blade. The blade’s electric arc slices through frame with burning ember trails that streak through the air against the pulsing hex arena.' },
  { slug: 'DINER - NIGHT', sh: 'INT. DINER - NIGHT', line: "Rain on the window. MARA, 40s, sits alone.\nMARA:\nYou came back.\nA long beat. The door chimes.DANIEL:\nI shouldn't have." },
  { slug: 'WORKSHOP - DAY', sh: 'INT. WORKSHOP - DAY', line: `An extreme, highly detailed close-up of a weather-beaten artisan's hands in a dark, dusty metal workshop. They are using a heavy grinder on a piece of dark steel. An explosive shower of bright, warm amber sparks violently illuminates the deep shadows of the room` },
  { slug: 'CLUB - EVENING', sh: 'INT. CLUB - EVENING', line: 'Medium shot of the Lady in Red posing a question to Karthik.' },
  { slug: 'BAR - CLOSE', sh: 'INT. BAR - CLOSE', line: "An intimate, highly detailed close-up of a weary, rugged male protagonist sitting in a dimly lit, smoky 1950s jazz club." },
  { slug: 'STADIUM - NIGHT', sh: 'EXT. STADIUM - NIGHT', line: 'A lone, athlete-warrior stands in the center of a colossal, pitch-black stadium. They grip a sleek, flat, bat-like cybernetic weapon, resting it on their shoulder. Heavy atmospheric dust swirls around them.' },
];

const gridVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.07 } },
};
const tileVariants = {
  hidden: { opacity: 0, y: 44, scale: 0.96 },
  visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.9, ease } },
};

function GalleryTile({ g, i }: { g: (typeof GALLERY)[number]; i: number }) {
  const [tapped, setTapped] = useState(false);
  return (
    /* tile class drives CSS :hover rule; data-active drives tap on mobile */
    <motion.div
      className="tile relative [aspect-ratio:2.35/1] bg-black overflow-hidden cursor-pointer"
      variants={tileVariants}
      data-active={tapped ? '' : undefined}
      onClick={() => setTapped(t => !t)}
      onMouseLeave={() => setTapped(false)}
    >
      <CdnImage
        src={GALLERY_IMAGES[i % GALLERY_IMAGES.length]}
        alt={`AI-generated film frame — ${g.sh}`}
        loading="lazy"
        width={1600}
        height={680}
        sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
        className="w-full h-full object-cover block"
      />
      <span className="absolute top-[10px] left-3 font-mono text-[9px] tracking-[0.2em] text-[rgba(255,255,255,0.5)] uppercase z-[2]">
        INDUCE · FRAME {String(i + 1).padStart(3, '0')}
      </span>
      <span className="absolute top-[10px] right-3 font-mono text-[9px] tracking-[0.2em] text-[rgba(255,255,255,0.5)] uppercase z-[2]">
        / TAP
      </span>
      transform + transition handled entirely in CSS to avoid Tailwind v4 translate/transform conflict
      <div className="script-line absolute inset-0 grid place-items-center p-6 bg-[rgba(10,10,10,0.88)] [backdrop-filter:blur(8px)] text-center">
        <div>
          <span className="block font-mono text-[9px] tracking-[0.22em] text-primary uppercase mb-[10px]">{g.sh}</span>
          <span className="font-mono text-[9px] text-on-surface leading-[1.5] max-w-[90%] block">"{g.line}"</span>
        </div>
      </div>
    </motion.div>
  );
}

export function Gallery() {
  return (
    <section className="section band-light low">
      <div className="container">
        <motion.div
          className="flex justify-between items-end gap-10 mb-12 max-md:flex-col max-md:items-start"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 1, ease }}
        >
          <h2 className="home-section-title m-0 max-w-[18ch]">
            Every frame here was <em>verified before the cut.</em>
          </h2>
          <p className="body-lg max-w-[36ch]">Six shots, six models, one continuous story. Not one of them reached the timeline unchecked.</p>
        </motion.div>

        <motion.div
          className="grid grid-cols-3 gap-3 max-lg:grid-cols-2 max-sm:grid-cols-1"
          variants={gridVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
        >
          {GALLERY.map((g, i) => <GalleryTile key={i} g={g} i={i} />)}
        </motion.div>
      </div>
    </section>
  );
}
